// Dip Simple-arm Build Your Routine - the multi-product usage chooser (the Simple theme).
// Plan of record: docs/dip-simple-home-routine-plan.md; offer build
// docs/dip-simple-offer-rollout-build.md. One question set per selected product assigns each
// product its own exact-fit pack + cadence (the same maths as the PDP chooser in
// dip-simple-buy.js: assets/dip-plan-engine.js, never duplicated here), then the ORDER SUMMARY
// (Charlie 2026-09-06: the live site's build summary card, .dip-recap--card + .dip-svwrap +
// .dip-total): every product itemised on its own plan with its own roll-on line (offer decision
// ruling 15), the offer's pound saving as a subtraction (ruling 13), the first-order total. Every
// selected product asks its own question (fabric conditioner included); no tiers or upsells here.
// The "deliveries together" mode is GONE (ruling 9, build phase 4): order 1 ships together
// anyway and month-snapped cadences merge in Recharge; aligning deliveries is a portal job,
// never a purchase-flow question. A multi-surface line brings its bottle + cloth exactly as on
// the MSC page (ruling 9, assets/dip-hardware.js). The welcome kit left this page on
// 2026-09-04 (ruling 1, phase 2). Everything money-bearing is computed from the data island.

import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { initGallery } from '@theme/dip-gallery';
import '@theme/dip-tabs';
import { initStickyBar } from '@theme/dip-sticky-bar';
import {
  WPM, cadenceFor, planFor, basePack, cadenceLabel, variantFor, packsOf, companionBooking,
} from '@theme/dip-plan-engine';
// The first-order offer (assets/dip-offer.js): per-line first-order price + itemised roll-on
// line, the same module the PDPs use, so a line prices identically on both surfaces.
import { priceLine, firstLabel, withGroupShipping, renewalShipMinor } from '@theme/dip-offer';
// The multi-surface hardware (assets/dip-hardware.js): the same helper the PDPs use.
import {
  hwActive, hwLines, hwWorth, hwIncludedText, reconcileHardwareLines, publishHardwareCodes,
} from '@theme/dip-hardware';
// The Everything split test (docs/dip-everything-split-test.md): the visitor's arm for the cart
// tags, and the choice cards' pricing and painting (shared with the PDPs).
import { withArm, tagCart, evrOn, arm, hhOn } from '@theme/dip-arm';
import { split as hhSplit, paintBar as hhPaintBar, lineNote as hhLineNote, dueText as hhDueText } from '@theme/dip-hh';
import { quoteAll, paintEverything as paintEvr, bindChoice, scrollToStep } from '@theme/dip-evr';

/** Read the basket's item count out of rendered section HTML.
 *  Duplicated byte-identically in dip-buy-box.js, dip-home.js, dip-simple-buy.js; keep in sync. */
const dipCartCountFromSections = (sections) => {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const match = html.match(/data-testid="cart-bubble"[^>]*>\s*([\d,]+)/);
    if (match) return parseInt(match[1].replace(/,/g, ''), 10);
  }
  return null;
};

const CURRENCY = window.Shopify?.currency?.active || 'GBP';
const LOCALE = document.documentElement.lang || 'en';
const _fmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY });
const MINOR = 100;
const money = (minorUnits) => _fmt.format((minorUnits || 0) / MINOR);
const _fmt0 = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY, minimumFractionDigits: 0, maximumFractionDigits: 0 });
const moneyTidy = (minorUnits) => ((minorUnits || 0) % MINOR === 0 ? _fmt0 : _fmt).format((minorUnits || 0) / MINOR);
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// The canonical .dip-box tick (the bundle pick cards / the PDP tier lines).
const BOX_TICK = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.2" aria-hidden="true"><path d="M4.5 12.5 10 18 19.5 6.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// The stroked button arrow (snippets/dip-btn-arrow.liquid is the source; keep the path in step).
const BTN_ARROW = '<svg class="dip-btn__arrow" viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3.5 10h13M11 4.5l5.5 5.5-5.5 5.5"/></svg>';
// The order summary's row tick (the live bundle page's .dip-recap-t mark).
const RECAP_TICK = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" aria-hidden="true"><path d="M4.5 12.5 10 18 19.5 6.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const PRODUCT_KEYS = ['laundry', 'dishwasher', 'fabcon', 'msc'];
// A PDP's Everything path asks about the page's own product first (Charlie 2026-09-30): the
// dishwasher and multi-surface pages lead with their own question. The conditioner page keeps
// laundry first (in arm B the conditioner copies the laundry answers and is not asked).
const LEADS_OWN_STEP = ['dishwasher', 'msc'];

/**
 * Where a routine-page visit came from, for the Everything test's read (docs/dip-everything-split-test.md
 * §2e): the homepage Everything card links with ?dip_from=home-range; any other arrival from the homepage
 * (the routine band, the menu) reads 'home'. While the test is on (an arm is set) or Everything shows on the
 * routine page (mode routine, 15 Oct 2026), so the homepage card's route stays readable; '' otherwise.
 */
function entryFrom() {
  if (!arm() && !evrOn()) return '';
  const q = new URLSearchParams(location.search).get('dip_from');
  if (q && /^[a-z0-9-]{1,24}$/.test(q)) return q;
  try {
    const r = new URL(document.referrer);
    if (r.origin === location.origin && r.pathname === '/') return 'home';
  } catch { /* no referrer */ }
  return '';
}

class DipRoutine extends HTMLElement {
  connectedCallback() {
    try { this.model = JSON.parse(this.querySelector('[data-dip-data]').textContent); } catch { this.model = null; }
    if (!this.model) return;
    this.labels = this.model.labels || {};
    // The first-order offer's display state (snippets/dip-offer.liquid); `active` is already
    // false for a logged-in past purchaser.
    this.offer = this.model.offer || { active: false };
    // The multi-surface hardware (snippets/dip-msc-hardware.liquid).
    this.hardware = this.model.hardware || null;
    this.freeShipMinor = Number(this.dataset.freeShipMinor || 0);

    // The Everything test. `embed` = this chooser is a PDP's Everything path (snippets/
    // dip-routine-chooser.liquid, embed: true): every product on, no pick step, numbered after the
    // PDP's choice step, driven by the PDP (start / submit / paintEverything). An arm-A visitor
    // never sees it, so it does no work at all.
    this.embed = this.hasAttribute('data-embed');
    if (this.embed && !evrOn()) return;
    this.src = this.dataset.src || 'routine';
    this.from = this.embed ? '' : entryFrom();
    this.stepOffset = Number(this.dataset.stepOffset || 0);
    this.evrChoice = null;

    // refs: every [ref] under this element, first one wins.
    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => {
      const k = el.getAttribute('ref');
      if (!(k in this.refs)) this.refs[k] = el;
    });
    // The routine page's choice step is arm B's alone: arm A walks today's page untouched.
    if (this.refs.evrStep && !evrOn()) { this.refs.evrStep.remove(); delete this.refs.evrStep; }
    // Arm B's rules (Everything path on a PDP, or the routine page with its choice step): fabric
    // conditioner matched to the laundry, the bottle and cloth counted in the saving, the next step's
    // top scrolled into view, the floating button's own wording. Arm A keeps today's behaviour.
    this.evrMode = this.embed || !!this.refs.evrStep;
    this.E = this.model.evr || {};
    if (this.evrMode) this.classList.add('is-evr');
    this.evrConfirmed = null;

    // State. Nothing pre-selected (the strategy line, verbatim); the PDP Everything path has
    // every product on by definition.
    this.on = { laundry: false, dishwasher: false, fabcon: false, msc: false };
    if (this.embed) PRODUCT_KEYS.forEach((k) => { if (this.model.products[k]) this.on[k] = true; });
    this.answers = {};
    PRODUCT_KEYS.forEach((k) => {
      const p = this.model.products[k];
      if (!p) return;
      this.answers[k] = { washes: Number(p.defaultWashes || 4), factor: Number(p.fixedFactor || 1.5), scent: p.scents?.[0] || '' };
    });
    // Arm B: the dishwasher starts from the dishwasher PDP's own default (5 runs a week), so its 60
    // washes land every 12 weeks in the same week wording as the other products (Charlie 2026-09-30).
    if (this.evrMode && this.answers.dishwasher && Number(this.E.dwRuns) > 0) {
      this.answers.dishwasher.washes = Number(this.E.dwRuns);
      const range = this.refs.step_dishwasher?.querySelector('[data-range]');
      const read = this.refs.step_dishwasher?.querySelector('[data-read]');
      if (range) range.value = String(this.E.dwRuns);
      if (read) read.textContent = String(this.E.dwRuns);
    }
    this.revealed = false;

    // The page's own product first on a PDP's Everything path (steps run in DOM order; the numbers follow).
    const lead = this.embed ? this.dataset.lead : '';
    this.leadKey = PRODUCT_KEYS.includes(lead) ? lead : '';
    if (LEADS_OWN_STEP.includes(lead) && this.refs[`step_${lead}`] && this.refs.step_laundry) {
      this.refs.step_laundry.before(this.refs[`step_${lead}`]);
    }
    this.scentColours = this.model.scentColours || {};
    this.#bind();
    // The PDP's Everything path: every product on, so only the conditioner's visibility can change.
    if (this.embed) this.#syncStepVisibility();
    // Arm B on the routine page: the choice step leads, the pick step waits behind it.
    // Nothing after it shows until the path is chosen (Charlie 2026-09-30: "Pick your products" as
    // step 2 made no sense beside Everything). Everything may start ticked (dip_evr_rtn_preselect).
    if (this.refs.evrStep) {
      this.refs.pickStep?.classList.remove('is-active');
      this.refs.pickStep?.classList.add('is-locked');
      this.refs.pickStep?.querySelector('.dip-sstep__head')?.setAttribute('aria-expanded', 'false');
      if (this.refs.pickStep) this.refs.pickStep.hidden = true;
      if (this.#planStep()) this.#planStep().hidden = true;
      const pre = this.refs.evrStep.querySelector('input[name="dip-evr"]:checked');
      if (pre) this.evrChoice = pre.value;
      this.paintEverything(this.refs.evrStep);
      this.classList.add('is-evr-ready');
    }
    this.#renumber();
    this.#initTabsets();
    this.#paintScentPills();
    this.#applyScent();
    if (this.refs.gallery) initGallery({ track: this.refs.gallery, dots: this.refs.dots, thumbs: this.refs.thumbs, label: 'image' });
    if (this.refs.stickybar) {
      this.stickyBarEl = initStickyBar({ bar: this.refs.stickybar, anchor: this.refs.flow || this });
      this.#renderSticky();
    }
    this.#publishGuardConfig();
  }

  // [data-dip-tabs] binder for the accordion tabsets (mirrors dip-simple-buy.js #initTabsets;
  // keep the copies in sync).
  #initTabsets() {
    this.querySelectorAll('[data-dip-tabs]').forEach((set) => {
      const tabs = [...set.querySelectorAll('[role="tab"]')];
      const panels = tabs.map((t) => set.querySelector(`#${CSS.escape(t.getAttribute('aria-controls'))}`));
      const select = (n) => {
        const idx = (n + tabs.length) % tabs.length;
        tabs.forEach((t, ti) => {
          const on = ti === idx;
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          if (panels[ti]) { panels[ti].hidden = !on; panels[ti].classList.toggle('is-hidden', !on); }
        });
      };
      tabs.forEach((t, ti) => {
        t.addEventListener('click', () => select(ti));
        t.addEventListener('keydown', (e) => {
          const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          const next = (ti + dir + tabs.length) % tabs.length;
          select(next); tabs[next].focus();
        });
      });
    });
  }

  // Two-state floating bar (mirrors the Simple PDPs): PICK MODE before the reveal, a single
  // offer button that scrolls back to the chooser; price + Add-to-basket after.
  #renderSticky() {
    if (!this.refs.stickybar) return;
    this.refs.stickybar.classList.toggle('dip-stickybar--pick', !this.revealed);
    const cta = this.refs.stickyCta;
    if (!cta) return;
    if (this.revealed) {
      cta.className = 'dip-btn dip-sb-cta';
      cta.textContent = this.#L('sticky_cta', 'Add to basket');
    } else {
      cta.className = 'dip-btn dip-btn--lg dip-btn--offer dip-sb-cta-pick';
      cta.innerHTML = `<span class="dip-btn__label"></span>${BTN_ARROW}`;
      cta.querySelector('.dip-btn__label').textContent = this.evrMode && this.E.sticky ? this.E.sticky : this.#L('pickWord', 'Build your routine');
    }
  }

  #L(key, fallback) { return this.labels[key] || fallback; }

  // The PDPs' scent-pair theming (mirrors dip-simple-buy.js): the chosen laundry scent inks
  // the whole planner; before/without a laundry choice the section holds Fresh linen's pair.
  #applyScent() {
    const sc = this.scentColours[this.answers.laundry?.scent];
    if (!sc) return;
    this.closest('.dip-rtn')?.style.setProperty('--sel', sc.sel || '#F4F7FB');
    this.closest('.dip-rtn')?.style.setProperty('--sel-ink', sc.selInk || 'var(--dip-accent-ink)');
  }

  #paintScentPills() {
    this.querySelectorAll('input[name="dip-rtn-scent-laundry"]').forEach((r) => {
      const label = r.closest('.dip-scent-pill');
      const col = this.scentColours[r.value] || {};
      label?.style.setProperty('--st', col.fill || 'var(--dip-accent)');
      label?.style.setProperty('--stb', col.border || 'var(--dip-accent-ink)');
    });
  }

  // ---- Maths (the shared engine) -------------------------------------------

  #rate(key) {
    const a = this.answers[key];
    const p = this.model.products[key];
    if (!a || !p) return 0;
    // Fabric conditioner answers its own question (Charlie 2026-09-06: every selected product has
    // its own), factor 1: conditioned washes, not load-scaled.
    if (key === 'msc') {
      // Bottles a month -> packs a week (a pack of 4 sheets = 4 bottles): n / 4 / WPM.
      return (a.washes / 4) / WPM;
    }
    return a.washes * (key === 'laundry' ? a.factor : Number(p.fixedFactor || 1));
  }

  #selected() { return PRODUCT_KEYS.filter((k) => this.on[k] && this.model.products[k]); }

  /** One line per selected product, each on its own exact plan. Null while unanswerable. */
  #lines() {
    const sel = this.#selected();
    if (!sel.length) return null;
    const lines = [];
    for (const key of sel) {
      const p = this.model.products[key];
      const rate = this.#rate(key);
      if (!rate) return null;
      let pack; let variant; let plan;
      const lead = key === 'fabcon' && this.#fabMirrored() ? lines.find((x) => x.key === 'laundry') : null;
      if (lead) {
        // Matched to the detergent: the laundry's pack and cadence (the engine's mirror rule, the
        // same one the laundry PDP's conditioner question uses).
        const booked = companionBooking({ ...p, mirror: true }, {
          scent: p.scents?.[0] || '', mainPack: lead.pack, mainCad: lead.plan.cad, mainRate: this.#rate('laundry'),
        });
        variant = booked?.variant;
        pack = Number(variant?.washes) || 0;
        plan = variant ? planFor(variant, booked.cad) : null;
      } else {
        pack = basePack(packsOf(p), rate);
        variant = variantFor(p.variants, pack, this.answers[key]?.scent || '');
        plan = planFor(variant, cadenceFor(pack, rate));
      }
      if (!variant || !plan) return null;
      const label = cadenceLabel(plan.cad);
      const hwOn = p.isMsc && hwActive(this.hardware);
      lines.push({
        key, p, rate, pack, variant, plan, label,
        price: priceLine(plan.raw, label, this.offer, variant, money),
        hw: hwOn ? { lines: hwLines(this.hardware, variant.size), text: hwIncludedText(this.hardware, variant.size), worth: hwWorth(this.hardware, variant.size) } : null,
      });
    }
    // Lines that share a cadence renew in one parcel: their roll-on delivery is judged on the
    // parcel's price, not each line's (dishwasher 60 + conditioner 60 every 4 weeks ships free).
    return withGroupShipping(lines, this.offer, money);
  }

  #totals(lines) {
    if (!lines) return null;
    // `firstBill` is what the FIRST order charges (each line at its first-order price);
    // `renewBill` the sum of the plan prices, which is what the lines roll on to.
    const renewBill = lines.reduce((t, l) => t + (l.plan?.raw || 0), 0);
    const firstBill = lines.reduce((t, l) => t + (l.price ? l.price.first : (l.plan?.raw || 0)), 0);
    const otBill = lines.reduce((t, l) => t + (Number(l.variant?.otRaw) || 0), 0);
    const offerOn = firstBill < renewBill;
    return { firstBill, renewBill, otBill, saving: renewBill - firstBill, offerOn };
  }

  // ---- Steps --------------------------------------------------------------

  #steps() { return [...this.querySelectorAll('.dip-sstep')].filter((el) => !el.hidden); }

  #renumber() {
    this.#steps().forEach((el, i) => {
      const n = el.querySelector('.dip-sstep__n');
      if (n) n.textContent = String(i + 1 + this.stepOffset);
    });
  }

  // ---- The Everything test (public: the PDP drives an embedded chooser) ---------------------

  /** Open the first question (the PDP calls this when the shopper chooses Everything). */
  start() {
    const first = this.#steps()[0];
    if (!first) return;
    this.#renumber();
    if (!this.#steps().some((el) => el.classList.contains('is-done'))) this.#setActive(first);
    this.render();
  }

  #planStep() { return this.querySelector('.dip-sstep[data-step="plan"]'); }

  // The PDP that embeds this chooser mirrors its floating bar from here.
  /**
   * Half now, half later arm B (review only until Charlie signs it, ?hhsave=1): the savings box shows only what is
   * free whether or not the customer keeps going: the glass bottle and cloth at their worth, and the first delivery
   * when the FULL-PRICE plan reaches the free-delivery threshold (below it the balance service bills it on day 14).
   * Never the 50%: in arm B that is not a saving for anyone who stays. Values at full worth, not subtractions.
   */
  #paintHhFreeBox(lines, totals, money) {
    const box = this.refs.savings;
    if (!box) return;
    // On by the setting (default on, Charlie 17 Oct); ?hhsave=0 / 1 forces it off / on for review on a preview.
    const q = new URLSearchParams(location.search).get('hhsave');
    const on = hhOn() && totals.offerOn && (q === '1' || (q !== '0' && this.offer?.hh?.freeBox !== false));
    box.classList.toggle('dip-svwrap--hh', Boolean(on));
    const h = box.querySelector('.dip-sv-h');
    const tdt = box.querySelector('.dip-sv-row--total dt');
    if (h && !h.dataset.a) h.dataset.a = h.textContent;
    if (tdt && !tdt.dataset.a) tdt.dataset.a = tdt.textContent;
    if (!on) { if (h) h.textContent = h.dataset.a; if (tdt) tdt.textContent = tdt.dataset.a; return; }
    const H = this.offer?.hh || {};
    const hw = (lines || []).reduce((t, l) => t + (Number(l.hw?.worth) || 0), 0);
    const full = (lines || []).reduce((t, l) => t + (Number(l.plan?.raw) || 0), 0);
    const ship = Number(this.offer?.shipMinor) || 0;
    const shipFree = ship > 0 && renewalShipMinor(full, this.offer) === 0 ? ship : 0;
    const total = hw + shipFree;
    box.hidden = total <= 0;
    if (h) h.textContent = H.freeBoxTitle || 'Free with your first order';
    if (tdt) tdt.textContent = H.freeBoxTotal || 'Total free';
    if (this.refs.svOfferRow) this.refs.svOfferRow.hidden = true;
    if (this.refs.svHwRow) this.refs.svHwRow.hidden = !hw;
    if (this.refs.svHw) this.refs.svHw.textContent = money(hw);
    const hwDt = this.refs.svHwRow?.querySelector('dt');
    if (hwDt) { if (!hwDt.dataset.a) hwDt.dataset.a = hwDt.textContent; hwDt.textContent = H.freeBoxHw || 'Glass spray bottle + microfibre cloth'; }
    if (this.refs.svShipRow) this.refs.svShipRow.hidden = !shipFree;
    if (this.refs.svShip) this.refs.svShip.textContent = money(shipFree);
    const shipDt = this.refs.svShipRow?.querySelector('dt');
    if (shipDt) { if (!shipDt.dataset.a) shipDt.dataset.a = shipDt.textContent; shipDt.textContent = H.freeBoxShip || 'First delivery'; }
    if (this.refs.svTotal) this.refs.svTotal.textContent = money(total);
  }

  #emitState() {
    const totals = this.totals; const lines = this.lines;
    if (!this.embed || !totals) return;
    const billed = (lines && lines.length === 1 && lines[0].price) ? lines[0].price.rollOn
      : (totals.offerOn ? this.#L('sticky_multi', 'then each product at its own price, listed above') : this.#L('sticky_exact', 'Matched per product'));
    this.dispatchEvent(new CustomEvent('dip:routine-state', {
      detail: { revealed: this.revealed, first: totals.firstBill, renew: totals.renewBill, offerOn: totals.offerOn, billed, label: firstLabel(this.offer) },
    }));
  }

  /** Arm B: fabric conditioner rides the laundry (pack and cadence) whenever both are in the routine. */
  #fabMirrored() { return !!(this.evrMode && this.on.laundry && this.on.fabcon && this.model.products.laundry && this.model.products.fabcon); }

  /** Add the routine to the basket (the PDP's floating bar calls this once the plan shows). */
  submit() { return this.#addToCart(true); }

  get isRevealed() { return !!this.revealed; }

  /** The cheapest first order this chooser can book: every product at its smallest pack. */
  quote() {
    return quoteAll({
      products: this.model.products, offer: this.offer, hardware: this.hardware,
      freeShipMinor: this.freeShipMinor, money, rateOf: (k) => this.#rate(k),
    });
  }

  /**
   * Paint the Everything card in a choice step (this page's, or a PDP's). `single` = the
   * one-product card's first-order figure on a PDP (minor units), for the step-up bullet.
   */
  paintEverything(scope) {
    const q = this.quote();
    if (q) paintEvr(scope, q, { E: this.E, offer: this.offer, money });
    return q;
  }

  /**
   * Back to a clean start below the choice (round 3, Charlie 2026-09-30: switching paths must not
   * carry the other path's progress). Every step after the choice is closed and un-done, the plan
   * is hidden again, the floating bar returns to its pick state. Answers (sliders, scent) are kept.
   */
  reset() {
    this.revealed = false;
    this.#steps().concat([...this.querySelectorAll('.dip-sstep[hidden]')]).forEach((el) => {
      if (el === this.refs.evrStep) return;
      el.classList.remove('is-done', 'is-active');
      el.classList.add('is-locked');
      el.querySelector('.dip-sstep__head')?.setAttribute('aria-expanded', 'false');
    });
    this.#renderSticky();
    this.#emitState();
  }

  // Reopening the choice hides everything under it until the choice is confirmed again.
  #hideBelowChoice() {
    this.querySelectorAll('.dip-sstep').forEach((el) => { if (el !== this.refs.evrStep) el.hidden = true; });
  }

  #confirmChoice() {
    const step = this.refs.evrStep;
    const v = this.evrChoice;
    if (!step || !v) return;
    const picked = step.querySelector('input[name="dip-evr"]:checked');
    if (this.refs.evrAns) this.refs.evrAns.textContent = picked?.dataset.title || '';
    const changed = this.evrConfirmed !== null && this.evrConfirmed !== v;
    this.evrConfirmed = v;
    if (changed) {
      // A different path starts fresh: nothing done, no plan; "choose my own" starts with no picks.
      this.reset();
      if (v === 'own') PRODUCT_KEYS.forEach((k) => { this.on[k] = false; });
      this.querySelectorAll('[data-pick]').forEach((btn) => {
        btn.classList.toggle('is-selected', !!this.on[btn.dataset.pick]);
        btn.setAttribute('aria-pressed', this.on[btn.dataset.pick] ? 'true' : 'false');
      });
      if (this.refs.pickAns) this.refs.pickAns.textContent = '';
    }
    if (v === 'all') {
      PRODUCT_KEYS.forEach((k) => { if (this.model.products[k]) this.on[k] = true; });
      this.querySelectorAll('[data-pick]').forEach((btn) => {
        btn.classList.toggle('is-selected', !!this.on[btn.dataset.pick]);
        btn.setAttribute('aria-pressed', this.on[btn.dataset.pick] ? 'true' : 'false');
      });
      if (this.refs.pickStep) this.refs.pickStep.hidden = true;
    } else if (this.refs.pickStep) {
      this.refs.pickStep.hidden = false;
    }
    if (this.#planStep()) this.#planStep().hidden = false;
    this.#syncStepVisibility();
    this.#advanceFrom(step);
  }

  #setActive(stepEl) {
    this.#steps().forEach((el) => {
      const active = el === stepEl;
      el.classList.toggle('is-active', active);
      el.classList.toggle('is-locked', !active && !el.classList.contains('is-done'));
      const head = el.querySelector('.dip-sstep__head');
      if (head?.setAttribute) head.setAttribute('aria-expanded', active ? 'true' : 'false');
    });
    // Arm B: the top of the opened step, not its Next button (Charlie 2026-09-30).
    if (this.evrMode) scrollToStep(stepEl);
    else if (stepEl?.scrollIntoView) stepEl.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    this.dispatchEvent(new Event('resize', { bubbles: true }));
  }

  #advanceFrom(stepEl) {
    stepEl.classList.add('is-done');
    const steps = this.#steps();
    const i = steps.indexOf(stepEl);
    const next = steps[i + 1];
    if (!next) return;
    if (next.dataset.step === 'plan') { this.revealed = true; this.#renderSticky(); }
    this.#setActive(next);
    this.render();
    this.#track('routine', { step: next.dataset.step });
  }

  // ---- Bind ---------------------------------------------------------------

  #bind() {
    // The Everything choice step (arm B): a card selects, Next confirms.
    if (this.refs.evrStep) {
      bindChoice(this.refs.evrStep, (v) => { this.evrChoice = v; this.#track('evr', { choice: v }); });
      this.refs.evrNext?.addEventListener('click', () => this.#confirmChoice());
      this.refs.evrHead?.addEventListener('click', () => {
        if (this.refs.evrStep.classList.contains('is-done')) this.#hideBelowChoice();
      });
    }
    // Product cards
    this.querySelectorAll('[data-pick]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.pick;
        this.on[key] = !this.on[key];
        btn.classList.toggle('is-selected', this.on[key]);
        btn.setAttribute('aria-pressed', this.on[key] ? 'true' : 'false');
        this.#syncStepVisibility();
        this.render();
      });
    });
    this.refs.pickNext?.addEventListener('click', () => {
      if (!this.#selected().length) return;
      this.#answerFor('pick');
      this.#advanceFrom(this.refs.pickStep);
    });

    // Per-product question steps
    PRODUCT_KEYS.forEach((key) => {
      const step = this.refs[`step_${key}`];
      if (!step) return;
      const range = step.querySelector('[data-range]');
      const read = step.querySelector('[data-read]');
      range?.addEventListener('input', () => {
        this.answers[key].washes = Number(range.value);
        if (read) read.textContent = range.value === range.max ? `${range.value}+` : range.value;
        this.#paintRange(range);
        if (key === 'laundry') this.#syncFabconNote();
        if (this.revealed) this.render();
      });
      if (range) this.#paintRange(range);
      step.querySelectorAll('input[name="dip-rtn-load"]').forEach((r) => {
        r.addEventListener('change', () => {
          this.answers.laundry.factor = Number(r.value);
          step.querySelectorAll('.dip-pack-option').forEach((l) => l.classList.toggle('is-selected', l.contains(r)));
          if (this.revealed) this.render();
        });
      });
      step.querySelectorAll(`input[name="dip-rtn-scent-${key}"]`).forEach((r) => {
        r.addEventListener('change', () => {
          this.answers[key].scent = r.value;
          step.querySelectorAll('.dip-scent-pill').forEach((l) => l.classList.toggle('is-selected', l.contains(r)));
          if (key === 'laundry') this.#applyScent();
          if (this.revealed) this.render();
        });
      });
      step.querySelector('[data-next]')?.addEventListener('click', () => {
        this.#answerFor(key);
        this.#advanceFrom(step);
      });
    });

    // Reopen a completed step
    this.querySelectorAll('.dip-sstep__head').forEach((head) => {
      head.addEventListener('click', () => {
        const step = head.closest('.dip-sstep');
        if (step.classList.contains('is-done') && !step.classList.contains('is-active')) this.#setActive(step);
      });
    });

    // Trustpilot ratings popover (same behaviour as the PDP: toggle, outside click, Escape)
    this.refs.tpToggle?.addEventListener('click', () => {
      const pop = this.refs.tpPop;
      if (!pop) return;
      pop.hidden = !pop.hidden;
      this.refs.tpToggle.setAttribute('aria-expanded', pop.hidden ? 'false' : 'true');
    });
    document.addEventListener('click', (e) => {
      const pop = this.refs.tpPop;
      if (pop && !pop.hidden && !pop.contains(e.target) && e.target !== this.refs.tpToggle && !this.refs.tpToggle?.contains(e.target)) {
        pop.hidden = true;
        this.refs.tpToggle?.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.refs.tpPop && !this.refs.tpPop.hidden) {
        this.refs.tpPop.hidden = true;
        this.refs.tpToggle?.setAttribute('aria-expanded', 'false');
      }
    });

    // CTA + one-time
    this.refs.form?.addEventListener('submit', (e) => { e.preventDefault(); this.#addToCart(true); });
    this.refs.cta?.addEventListener('click', (e) => { e.preventDefault(); this.#addToCart(true); });
    this.refs.stickyCta?.addEventListener('click', () => {
      if (!this.revealed) {
        (this.refs.flow || this).scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }
      this.#addToCart(true);
    });
    this.refs.once?.addEventListener('click', () => this.#addToCart(false));

    // How-your-subscription-works modal (PDP behaviour)
    // Embedded in a PDP, the dialog is the PDP's own (outside this element).
    const modal = this.refs.subModal || (this.embed ? this.closest('.dip-buy-section')?.querySelector('dialog.dip-modal') : null);
    if (this.refs.howLink && modal) {
      const supportsModal = typeof modal.showModal === 'function';
      const openM = () => { supportsModal ? modal.showModal() : modal.setAttribute('open', ''); };
      const closeM = () => { supportsModal ? modal.close() : modal.removeAttribute('open'); };
      this.refs.howLink.addEventListener('click', openM);
      this.refs.subModalClose?.addEventListener('click', closeM);
      modal.addEventListener('click', (e) => { if (e.target === modal) closeM(); });
    } else if (this.refs.howLink) {
      this.refs.howLink.hidden = true;
    }
  }

  #paintRange(range) {
    const min = Number(range.min || 1); const max = Number(range.max || 14);
    // --pf is the 0..1 fraction the shared slider CSS reads (dip-pdp.css .dip-srange__input);
    // this used to set an unread --fill, so the routine's fills sat at the CSS default.
    range.style.setProperty('--pf', String((Number(range.value) - min) / Math.max(max - min, 1)));
  }

  #syncStepVisibility() {
    PRODUCT_KEYS.forEach((key) => {
      const step = this.refs[`step_${key}`];
      if (!step) return;
      // Arm B: the conditioner question goes when the conditioner rides the laundry.
      const show = this.on[key] && !(key === 'fabcon' && this.#fabMirrored());
      step.hidden = !show;
      if (!show) step.classList.remove('is-active');
    });
    this.#syncFabconNote();
    this.#renumber();
  }

  // Kept as a no-op hook: the inherit note left with the inherit rule (2026-09-06).
  #syncFabconNote() {}

  #answerFor(key) {
    if (key === 'pick') {
      const names = this.#selected().map((k) => this.model.products[k].shortName || k);
      const ans = this.refs.pickAns;
      if (ans) ans.textContent = names.join(' · ');
      return;
    }
    const step = this.refs[`step_${key}`];
    const ans = step?.querySelector('.dip-sstep__ans');
    if (!ans) return;
    const a = this.answers[key];
    const tpl = this.model.products[key]?.ansText || '[n]';
    let txt = tpl.replace('[n]', String(a.washes));
    if (key === 'laundry') {
      const load = step.querySelector('input[name="dip-rtn-load"]:checked');
      const parts = [txt];
      if (a.scent) parts.unshift(a.scent);
      if (load?.dataset.title) parts.push(load.dataset.title.toLowerCase());
      txt = parts.join(' · ');
    }
    ans.textContent = txt;
  }

  // ---- Render -------------------------------------------------------------

  render() {
    this.#syncFabconNote();
    const sel = this.#selected();
    // The scarcity box: general wording until products are picked, then the tightest of the picks.
    window.__dipPlacesKeys = sel;
    document.dispatchEvent(new CustomEvent('dip:places-products', { detail: { keys: sel } }));
    if (this.refs.pickNext) this.refs.pickNext.disabled = !sel.length;

    const lines = this.#lines();
    const totals = this.#totals(lines);
    this.lines = lines;
    this.totals = totals;
    const L = this.labels;

    // The order summary (Charlie 2026-09-06, the live site's build summary card): one row per
    // product (title over pack, scent and cadence; the first-order figure and roll-on line on the
    // right), the multi-surface hardware row tagged FREE, then the savings stack and the price
    // block. Every figure computed from the island.
    if (this.refs.recapList && lines && totals) {
      // Each product row leads with its plan thumbnail (the theme's representative image, one
      // source for every surface: snippets/dip-product-island.liquid `thumb`); the tick stays on
      // rows without one (the hardware row).
      // Round 13 (Charlie 2026-09-08): the money line sits LEFT-ALIGNED under the size line (`price`); `value` is the
      // right-hand slot the hardware row still uses (FREE).
      const row = (title, detail, value = '', tag = '', thumb = '', price = '') => `<li>${thumb ? `<span class="dip-kit-ic dip-recap-thumb" aria-hidden="true"><img class="dip-kit-photo" src="${esc(thumb)}" alt="" loading="lazy" width="88" height="88"></span>` : ''}<span class="dip-recap-tx"><span class="dip-recap-t">${thumb ? '' : RECAP_TICK}${esc(title)}${tag}</span>${detail ? `<span class="dip-recap-s">${detail}</span>` : ''}${price ? `<span class="dip-recap-p">${price}</span>` : ''}</span>${value ? `<span class="dip-recap-v">${value}</span>` : ''}</li>`;
      const rows = [];
      // A PDP's Everything path lists the page's own product first in "In your plan" (Charlie 2026-09-30),
      // on all four PDPs; the rest keep the fixed order. Display only: the basket adds keep their order.
      const shown = this.leadKey ? [...lines].sort((a, b) => (b.key === this.leadKey) - (a.key === this.leadKey)) : lines;
      shown.forEach((l) => {
        const p = l.p || {};
        const sizeTpl = p.sizeText || '[n] washes';
        const size = sizeTpl.replace('[n]', String(l.pack)).replace('[size]', l.variant.size || '');
        const detail = [size, l.variant.scent || '', l.label].filter(Boolean).map(esc).join(' · ');
        const price = l.price?.on
          ? `<b>${money(l.price.first)} ${esc(firstLabel(this.offer))}</b> ${esc(l.price.rollOn)}`
          : `<b>${money(l.plan.raw)}</b> ${esc(l.price ? l.price.rollOn : l.label)}`;
        // Arm B: the products' full names ("Laundry sheets", "Multi-surface cleaning sheets kit"; round 3).
        const rowName = (this.evrMode && this.E.names?.[l.key]) || p.shortName || l.key;
        rows.push(row(rowName, detail, '', '', (p.thumbs && l.variant.scent && p.thumbs[l.variant.scent]) || p.thumb || '', price));
        if (l.hw?.text) {
          rows.push(row(l.hw.text, '', `<span class="dip-kit-free"><s>${moneyTidy(l.hw.worth)}</s> ${esc(L.freeWord || 'FREE')}</span>`));
        }
      });
      this.refs.recapList.innerHTML = rows.join('');

      // Savings stack: the offer as a subtraction, free first delivery only when the first order
      // would otherwise pay it (a real saving, never a decorative one), the total in the pink chip.
      const less = (v) => `\u2212${money(v)}`;
      const shipSaved = (totals.offerOn && this.offer.firstShipFree && this.freeShipMinor && totals.firstBill < this.freeShipMinor)
        ? (Number(this.offer.shipMinor) || 0) : 0;
      // Arm B: the free bottle and cloth at their worth join the saving (the Everything card's figure).
      const hwSaved = this.evrMode ? lines.reduce((t, l) => t + (Number(l.hw?.worth) || 0), 0) : 0;
      if (this.refs.svHwRow) this.refs.svHwRow.hidden = !hwSaved;
      if (this.refs.svHw) this.refs.svHw.textContent = less(hwSaved);
      const total = (totals.offerOn ? totals.saving : 0) + shipSaved + hwSaved;
      if (this.refs.savings) this.refs.savings.hidden = total <= 0;
      if (this.refs.svOfferRow) this.refs.svOfferRow.hidden = !totals.offerOn;
      if (this.refs.svOffer) this.refs.svOffer.textContent = less(totals.saving);
      if (this.refs.svShipRow) this.refs.svShipRow.hidden = !shipSaved;
      if (this.refs.svShip) this.refs.svShip.textContent = less(shipSaved);
      if (this.refs.svTotal) this.refs.svTotal.textContent = money(total);
      this.#paintHhFreeBox(lines, totals, money);

      // Price block: struck plan total over the first-order total (the offer display rule), or
      // the plain plan total; the roll-on clause beside it (one line, or the pointer to the rows).
      const single = lines.length === 1;
      if (this.refs.headline) {
        this.refs.headline.innerHTML = totals.offerOn
          ? `${money(totals.firstBill)} <small>${esc(firstLabel(this.offer))}</small>`
          : `${money(totals.renewBill)} <small>${esc(L.todayWord || 'today')}</small>`;
      }
      if (this.refs.was) {
        this.refs.was.hidden = !totals.offerOn;
        this.refs.was.textContent = totals.offerOn ? money(totals.renewBill) : '';
      }
      if (this.refs.billed) {
        // Charlie 2026-09-08: one sentence with the figure ("£20.00 first order, then each product at its own
        // price, listed above"), so the clause is joined with a comma when the offer is on.
        const clause = single && lines[0].price
          ? lines[0].price.rollOn
          : (totals.offerOn ? this.#L('sticky_multi', 'then each product at its own price, listed above') : '');
        this.refs.billed.textContent = clause ? (totals.offerOn ? `, ${clause}` : ` ${clause}`) : '';
      }
      // Half now, half later, arm B: the 14-day bar under the total.
      const hhThen = single && lines[0].price ? lines[0].price.rollOn : (totals.offerOn ? this.#L('sticky_multi', 'then each product at its own price, listed above') : '');
      hhPaintBar(this.refs.hhBar, hhSplit(lines.map((l) => l.price), this.offer), this.offer, money, hhThen);
    }

    // CTA + sticky
    if (totals) {
      const today = totals.firstBill;
      const label = `${this.#L('cta', 'Add to basket')} · ${money(today)}`;
      if (this.refs.cta) this.refs.cta.querySelector('.dip-btn__label').textContent = label;
      // The floating bar speaks the same grammar as the PDP bar: the first-order charge with its
      // label when the offer applies, and the roll-on line (one line) or a pointer to the itemised
      // lines above (several products, each with its own cadence).
      if (this.refs.stickyPrice) {
        this.refs.stickyPrice.textContent = totals.offerOn ? `${money(today)} ${firstLabel(this.offer)}` : money(today);
      }
      // Half now, half later arm B (Charlie, 16 Oct: "the floating bar copy needs updating to match the rest"): the
      // second line is what is due on the date ("£29.50 due 30 Oct"), the basket's own wording.
      const hhS = hhOn() && totals.offerOn ? hhSplit((lines || []).map((l) => l.price), this.offer) : null;
      if (this.refs.stickyBilled && hhS && hhS.rest > 0) {
        this.refs.stickyBilled.textContent = hhDueText(hhS, this.offer, money);
      } else if (this.refs.stickyBilled) {
        if (lines && lines.length === 1 && lines[0].price) {
          this.refs.stickyBilled.textContent = lines[0].price.rollOn;
        } else if (totals.offerOn) {
          this.refs.stickyBilled.textContent = this.#L('sticky_multi', 'then each product at its own price, listed above');
        } else {
          this.refs.stickyBilled.textContent = this.#L('sticky_exact', 'Matched per product');
        }
      }
      const ship = this.refs.shipNote;
      if (ship) {
        // First-order delivery: free under the offer (ruling 4) whenever it applies to a line;
        // else the threshold rule on what the first order actually charges.
        if (hhOn()) {
          // Half now, half later, arm B: the delivery is part of what is due on the date (the 14-day bar says so).
          ship.hidden = true;
        } else if (totals.offerOn && this.offer.firstShipFree) {
          // The savings stack already lists free first delivery when it saves money; the note
          // covers the case above the threshold, where the stack has no row for it.
          ship.textContent = this.#L('ship_first', 'Free delivery on your first order.');
          ship.hidden = !(this.freeShipMinor && today >= this.freeShipMinor);
        } else if (this.freeShipMinor && today >= this.freeShipMinor) {
          ship.textContent = this.#L('ship_free', 'Free delivery on this order.');
          ship.hidden = false;
        } else ship.hidden = true;
      }
      // Priced one-time button (PDP parity)
      if (this.refs.once) {
        // Plus the delivery a one-time order pays (threshold rule on its own total; Charlie, 16 Oct 2026).
        const otShip = renewalShipMinor(totals.otBill, this.offer);
        this.refs.once.textContent = `${this.#L('once', 'One-Time Purchase')} · ${money(totals.otBill)}${otShip ? ` + ${money(otShip)} delivery` : ''}`;
      }
    }
    this.#emitState();
    this.dispatchEvent(new Event('resize', { bubbles: true }));
  }

  /**
   * One itemised line (the shared .dip-slines row, the PDP tier grammar): the product and pack,
   * then the first-order figure and the roll-on line on its own row (ruling 15, verbatim); the
   * multi-surface line carries its included-hardware row.
   */
  #lineHtml(l) {
    const p = l.p || {};
    const name = p.shortName || l.key;
    const sizeTpl = p.sizeText || '[n] washes';
    const size = sizeTpl.replace('[n]', String(l.pack)).replace('[size]', l.variant.size || '');
    const value = l.price?.on
      ? `<b>${money(l.price.first)} ${esc(firstLabel(this.offer))}</b> · ${esc(l.price.rollOn)}`
      : esc(l.price ? l.price.rollOn : `${money(l.plan.raw)} · ${l.label}`);
    let html = `<li class="dip-sline" data-line-row="${esc(l.key)}">`
      + `<span class="dip-sline__pick is-fixed" aria-hidden="true"><span class="dip-box">${BOX_TICK}</span></span>`
      + `<span class="dip-sline__n">${esc(name)} · ${esc(size)}</span>`
      + `<span class="dip-sline__v">${value}</span></li>`;
    if (l.hw?.text) {
      html += `<li class="dip-sline dip-sline--hw" data-hw-for="${esc(l.key)}">`
        + `<span class="dip-sline__n">${esc(l.hw.text)}</span>`
        + `<span class="dip-sline__v"><span class="dip-kit-free"><s>${moneyTidy(l.hw.worth)}</s> ${esc(this.labels.freeWord || 'FREE')}</span></span></li>`;
    }
    return html;
  }

  // Gate instrumentation (routine completion by step, entry basket): a DOM event plus a
  // dataLayer push, stack-agnostic (mirrors dip-simple-buy.js #track).
  #track(name, extra = {}) {
    const detail = {
      surface: this.embed ? `${this.src}` : 'routine', products: this.#selected(), first: this.totals?.firstBill ?? null, ...extra,
    };
    try {
      document.dispatchEvent(new CustomEvent(`dip:${name}`, { detail, bubbles: true }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `dip_${name}`, ...detail });
    } catch { /* never let telemetry break the buy path */ }
  }

  // ---- Cart guard (assets/dip-msc-guard.js) ------------------------------

  // A multi-surface line chosen here brings its hardware exactly as on the MSC page (ruling 9):
  // publish the hardware model from this surface too, under its own slot (no code since fix b).
  #publishGuardConfig() {
    publishHardwareCodes('routine', this.hardware, this.model.products?.msc?.variants || []);
  }

  // ---- Cart ---------------------------------------------------------------

  // Exactly the chosen product lines, each on its plan when subscribing, plus the multi-surface
  // hardware (product hardware ships either way, in the per-size quantities the settings hold).
  #lineItems(subscribe) {
    const lines = this.lines || this.#lines() || [];
    const items = [];
    lines.forEach((l) => {
      // `_dip_src` (hidden line property, underscore = not rendered in the drawer or Recharge) records the
      // surface the add came from, so attribution can tell a PDP add from a routine-page add. Read by the
      // Monday health scan (Group E); never read by the theme or the cart guard.
      // The Everything test: the choice rides in the source tag (routine:everything, or the PDP's
      // pdp:<product>:everything), the visitor's arm in `_dip_arm` (assets/dip-arm.js).
      const src = this.embed ? this.src : (this.evrChoice === 'all' ? 'routine:everything' : 'routine');
      // `_dip_from`: a routine-page add that arrived from the homepage (entryFrom), so the test's read can
      // split the homepage's effect from the rest of the arm. Absent otherwise.
      const props = this.from ? { _dip_src: src, _dip_from: this.from } : { _dip_src: src };
      // Half now, half later, arm B: the visible line note the basket and checkout show.
      const note = subscribe ? hhLineNote(l.price, this.offer, money) : null;
      if (note) props[note.name] = note.value;
      const it = { id: Number(l.variant.id), quantity: 1, properties: withArm(props) };
      if (subscribe && l.plan?.id) it.selling_plan = Number(l.plan.id);
      items.push(it);
      if (l.hw?.lines?.length) items.push(...l.hw.lines);
    });
    return items;
  }

  async #addToCart(subscribe) {
    const lines = this.lines || this.#lines();
    if (!lines || !lines.length) return;
    const cta = subscribe ? this.refs.cta : this.refs.once;
    const items = await reconcileHardwareLines(this.#lineItems(subscribe), this.hardware, this.model.products?.msc?.variants || []);
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    cta?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript'),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      if (data.status) {
        this.#showError(data.description || data.message || 'Could not add to basket.');
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        return;
      }
      const itemCount = dipCartCountFromSections(data.sections);
      tagCart();
      this.#track('add', { subscribe, items: items.length });
      this.dispatchEvent(new CartAddEvent(undefined, this.id || 'dip-routine', {
        source: 'dip-routine', itemCount: itemCount ?? items.length, sections: data.sections,
      }));
    } catch {
      this.#showError('Could not add to basket. Please try again.');
    } finally {
      cta?.removeAttribute('aria-busy');
    }
  }

  #showError(msg) {
    const el = this.refs.error;
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(this._errT);
    this._errT = setTimeout(() => { el.hidden = true; }, 6000);
  }
}

if (!customElements.get('dip-routine')) customElements.define('dip-routine', DipRoutine);

// Reveal-on-scroll for this page's .dip-reveal elements (global-once, same as the PDPs).
if (!window.__dipRevealInit) {
  window.__dipRevealInit = true;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
  }, { threshold: 0.12 });
  document.querySelectorAll('.dip-reveal').forEach((el) => io.observe(el));
}
